import { CounterClockwiseClockIcon } from "@radix-ui/react-icons";
import { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";

import { IDexcomIntegration } from "../../../@types/admin/AdminDexcomIntegration";
import { TableSorted } from "../../../components/TableSorted";
import {
  DEXCOM_INTEGRATIONS_QUERY_KEY,
  useDexcomIntegrations,
} from "../../../hooks/queries/admin/dexcomIntegration";
import { useModal } from "../../../hooks/useModal";
import { getLongFormattedDate } from "../../../utils/dateFormatter";
import {
  ErrorMessage,
  FiltersContainer,
  PageContainer,
  PageHeader,
  PageTitle,
  RowAction,
  SearchInput,
  StatusBadge,
  TableContainer,
  TableLoadingState,
  TablePagination,
  TableWrapper,
  useMockPagination,
} from "../shared";
import { CellCaption, CellStack } from "./AdminTmpIntegration.styles";
import {
  connectionStatusTone,
  syncOutcomeLabel,
  syncOutcomeTone,
} from "./dexcomPresentation";
import { DexcomSyncHistoryModal } from "./DexcomSyncHistoryModal";

/** Search covers what an operator would paste in: ids, names, MRN, status. */
const SEARCH_FIELDS: (keyof IDexcomIntegration)[] = [
  "integrationId",
  "patientId",
  "patientName",
  "hospital",
  "mrn",
  "status",
];

/**
 * Dexcom integration (EN-5232): every patient who connected Dexcom, whether
 * the sync is keeping up (newest stored reading, latest attempt) and, per
 * patient, the history of sync attempts. Read-only, same shape as the
 * Jupyter page: one call returns every connection and search is client-side.
 */
export function AdminDexcomIntegration() {
  document.title = "Dexcom integration - Admin";

  const { openModal } = useModal();
  const { integrations, isLoading, isError } = useDexcomIntegrations();

  const {
    items,
    allItems,
    pagination,
    searchTerm,
    setSearchTerm,
    setPage,
    setPageSize,
  } = useMockPagination({ data: integrations, searchFields: SEARCH_FIELDS });

  const openHistory = (integration: IDexcomIntegration) => {
    openModal(<DexcomSyncHistoryModal integration={integration} />, {
      width: "min(1100px, 94vw)",
      maxHeight: "90vh",
      showCloseButton: true,
    });
  };

  const columns: ColumnDef<IDexcomIntegration, string>[] = useMemo(
    () => [
      {
        id: "patient",
        accessorFn: (i) => i.patientName ?? "",
        header: "Patient",
        cell: ({ row }) => (
          <CellStack>
            <span>{row.original.patientName || "-"}</span>
            <CellCaption>
              ID {row.original.patientId}
              {row.original.mrn ? ` · MRN ${row.original.mrn}` : ""}
            </CellCaption>
          </CellStack>
        ),
        enableSorting: true,
      },
      {
        accessorKey: "hospital",
        header: "Hospital",
        cell: ({ row }) => <span>{row.original.hospital || "-"}</span>,
        enableSorting: true,
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => (
          <CellStack title={row.original.revocationReason ?? undefined}>
            <StatusBadge status={connectionStatusTone(row.original.status)}>
              {row.original.status}
            </StatusBadge>
            {row.original.revokedAt && (
              <CellCaption>
                {getLongFormattedDate(row.original.revokedAt, "-")}
              </CellCaption>
            )}
          </CellStack>
        ),
        enableSorting: true,
      },
      {
        accessorKey: "created",
        header: "Connected on",
        cell: ({ row }) => (
          <span>{getLongFormattedDate(row.original.created, "-")}</span>
        ),
        enableSorting: true,
      },
      {
        // Sensor time of the newest stored reading: stale here while the
        // status says Connected means the sync is not keeping up.
        accessorKey: "latestReadingTime",
        header: "Newest reading",
        meta: { renderEmpty: true },
        cell: ({ row }) => (
          <span>
            {getLongFormattedDate(row.original.latestReadingTime, "—")}
          </span>
        ),
        enableSorting: true,
      },
      {
        accessorKey: "lastSyncTimestamp",
        header: "Last successful sync",
        meta: { renderEmpty: true },
        cell: ({ row }) => (
          <span>
            {getLongFormattedDate(row.original.lastSyncTimestamp, "—")}
          </span>
        ),
        enableSorting: true,
      },
      {
        id: "latestSync",
        accessorFn: (i) => i.latestSync?.ranAt ?? "",
        header: "Latest attempt",
        meta: { renderEmpty: true },
        cell: ({ row }) => {
          const log = row.original.latestSync;
          if (!log) return <span>—</span>;
          return (
            <CellStack title={log.error ?? undefined}>
              <StatusBadge status={syncOutcomeTone(log.outcome)}>
                {syncOutcomeLabel(log.outcome)}
              </StatusBadge>
              <CellCaption>
                {getLongFormattedDate(log.ranAt, "-")} · {log.readingsStored}{" "}
                stored
              </CellCaption>
            </CellStack>
          );
        },
        enableSorting: true,
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
          <RowAction
            label="Sync history"
            icon={<CounterClockwiseClockIcon />}
            onClick={() => openHistory(row.original)}
          />
        ),
      },
    ],
    []
  );

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Dexcom integration</PageTitle>
      </PageHeader>

      <FiltersContainer>
        <SearchInput
          type="text"
          placeholder="Search by integration ID, patient, hospital, MRN, or status..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </FiltersContainer>

      {isError && (
        <ErrorMessage>
          Failed to load Dexcom connections. Try refreshing the page.
        </ErrorMessage>
      )}

      <TableContainer>
        <TableWrapper refreshKeys={[[DEXCOM_INTEGRATIONS_QUERY_KEY]]}>
          <TableSorted
            columns={columns}
            data={items}
            emptyStateText={isLoading ? <TableLoadingState /> : "No rows found"}
          />
        </TableWrapper>
        <TablePagination
          exportCsv={{
            filename: "dexcom-integrations",
            columns,
            rows: allItems,
          }}
          pagination={pagination}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </TableContainer>
    </PageContainer>
  );
}
